import {
  REPORT_STATUSES,
  REPORT_TARGETS,
  type AdminReportListItem,
  type ReportStatus,
  type ReportTarget,
} from '@hemmafixa/shared';
import {
  Badge,
  DataTable,
  EmptyState,
  FilterChips,
  FormField,
  MasterDetail,
  PageHeader,
  Pagination,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  type ColumnDef,
} from '@hemmafixa/ui';
import { Flag, MousePointerClick } from 'lucide-react';
import { useMemo, useState } from 'react';
import { fill, useI18n } from '@/i18n/context';
import { DetailPanel, FullPageLink, SelectableCard } from '../shared/master-detail-parts';
import { QueryView } from '../shared/query-view';
import { useSelectedParam, useShownSelection } from '../shared/use-selected-param';
import { useAdminReports } from './api';
import { AdminReportDetail } from './report-page';

const PAGE_SIZE = 20;

/** Reports from clients and providers (P5-06): open ones oldest first, so none waits too long. */
export function ReportsPage() {
  const { t, relative } = useI18n();
  const [picked, setSelected] = useSelectedParam();
  const [status, setStatus] = useState<ReportStatus>('open');
  const [target, setTarget] = useState<ReportTarget | 'all'>('all');
  const [page, setPage] = useState(1);
  const r = t.admin.reports;
  const query = useAdminReports({
    status,
    target: target === 'all' ? undefined : target,
    page,
    pageSize: PAGE_SIZE,
  });
  const selected = useShownSelection(
    picked,
    query.data?.items.map((item) => item.id),
  );

  const columns = useMemo<ColumnDef<AdminReportListItem, unknown>[]>(
    () => [
      {
        accessorKey: 'target',
        header: r.columns.what,
        cell: ({ row }) => <Badge>{r.targets[row.original.target]}</Badge>,
      },
      {
        accessorKey: 'reason',
        header: r.columns.reason,
        cell: ({ row }) => t.report.reasons[row.original.reason],
      },
      {
        accessorKey: 'summary',
        header: r.columns.summary,
        enableSorting: false,
        cell: ({ row }) => <span className="line-clamp-2 max-w-md">{row.original.summary}</span>,
      },
      {
        id: 'subject',
        header: r.columns.subject,
        cell: ({ row }) => row.original.subject?.name ?? '',
      },
      {
        accessorKey: 'createdAt',
        header: r.columns.reported,
        cell: ({ row }) => relative(row.original.createdAt),
      },
    ],
    [r, t.report.reasons, relative],
  );

  return (
    <div className="grid gap-4">
      <PageHeader title={r.title} description={r.description} />
      <div className="flex flex-wrap items-center gap-2 [&_label]:sr-only [&_[role=combobox]]:h-9 [&_[role=combobox]]:rounded-xl [&_[role=combobox]]:border-transparent [&_[role=combobox]]:bg-surface [&_[role=combobox]]:shadow-sm">
        <FilterChips
          label={r.columns.status}
          value={status}
          onChange={(value) => {
            setStatus(value);
            setPage(1);
          }}
          options={REPORT_STATUSES.map((value) => ({ value, label: r.statuses[value] }))}
        />
        <FormField label={r.columns.what} className="w-full sm:w-56">
          <Select
            value={target}
            onValueChange={(value) => {
              setTarget(value as ReportTarget | 'all');
              setPage(1);
            }}
          >
            <SelectTrigger aria-label={r.columns.what}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t.common.all}</SelectItem>
              {REPORT_TARGETS.map((value) => (
                <SelectItem key={value} value={value}>
                  {r.targets[value]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>
        {/* Beside the filters, so the details start level with the list */}
        {selected && (
          <FullPageLink
            to={`/admin/reports/${selected}`}
            className="ml-auto hidden shrink-0 lg:inline-flex"
          />
        )}
      </div>
      <MasterDetail
        divided
        open={selected !== null}
        onClose={() => setSelected(null)}
        labels={{ details: r.title }}
        placeholder={<EmptyState icon={MousePointerClick} title={t.common.pickItem} />}
        detail={
          selected ? (
            <DetailPanel fullPage={`/admin/reports/${selected}`} linkBesideTabs>
              <AdminReportDetail id={selected} />
            </DetailPanel>
          ) : null
        }
        list={
          <>
            <QueryView query={query}>
              {(result) => (
                <>
                  <DataTable
                    layout="cards"
                    columns={columns}
                    data={result.items}
                    getRowId={(report) => report.id}
                    renderCard={(report) => (
                      <SelectableCard
                        to={`/admin/reports/${report.id}`}
                        selected={report.id === selected}
                        onSelect={() => setSelected(report.id)}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <p className="font-semibold">{t.report.reasons[report.reason]}</p>
                          <Badge>{r.targets[report.target]}</Badge>
                        </div>
                        <p className="line-clamp-2 text-sm">{report.summary}</p>
                        <p className="text-xs text-muted-foreground">
                          {[report.subject?.name, relative(report.createdAt)].filter(Boolean).join(' · ')}
                        </p>
                      </SelectableCard>
                    )}
                    empty={<EmptyState icon={Flag} title={r.empty} description={r.emptyText} />}
                  />
                  <Pagination
                    page={result.page}
                    pageSize={result.pageSize}
                    total={result.total}
                    onPageChange={setPage}
                    labels={{
                      previous: t.common.previous,
                      next: t.common.next,
                      showing: (from, to, total) => fill(t.common.showingRange, { from, to, total }),
                    }}
                  />
                </>
              )}
            </QueryView>
          </>
        }
      />
    </div>
  );
}
